import {
  createContext,
  type CSSProperties,
  type ReactNode,
  type Ref,
  useContext,
  useId,
} from 'react'
import styles from './AllJobsView.module.css'
import {
  ScreenHeaderBorder,
  ScreenHeaderContent,
  ScreenHeaderMain,
  ScreenHeaderPersistentTopBar,
  ScreenHeaderPersistentTopBarLayout,
  ScreenHeaderPersistentTopBarTitle,
  ScreenHeaderProvider,
  ScreenHeaderTitle,
} from '@/components/ScreenHeader'
import { Button } from '@/components/Button'
import { links } from '@/utils/link'
import { SectionHeadline } from '@/components/SectionHeadline'
import { useProvideViewportBackground } from '@/features/shell/global-navigation/GlobalNavigationContainer'
import { usePageTopOffset } from '@/contexts/page-top-offset-context'
import {
  Card,
  CardContent,
  CardContentDecoration,
  CardContentFooterActions,
  CardItemContent,
  CardItemContentDecoration,
  CardList,
  CardListItem,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import { Avatar, type AvatarProps } from '@/components/Avatar'
import { Badge } from '@/components/Badge'
import { Icon } from '@/components/Icon'
import {
  Dropdown,
  DropdownItem,
  DropdownItemTitle,
} from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { LabelChip } from '@/components/Chip'
import {
  AutoSkeleton,
  IsSkeletonContext,
  Skeleton,
  SkeletonColorContext,
  SkeletonList,
  useIsSkeleton,
  useSkeletonListContext,
} from '@/components/Skeleton'
import { useDocumentTitle } from '@/hooks/document-title'

export interface AllJobsViewProps {
  children?: ReactNode
  testId?: string
}

/**
 * Root component for the all-jobs views.
 */
export function AllJobsView({ children, testId }: AllJobsViewProps) {
  const pageTopOffset = usePageTopOffset()
  useProvideViewportBackground('secondary')
  useDocumentTitle('Your jobs')
  return (
    <ScreenHeaderProvider isOverlay>
      <div
        className={styles.root}
        style={{ '--page-top-offset': `${pageTopOffset}px` } as CSSProperties}
        data-test={testId}
      >
        {children}
      </div>
    </ScreenHeaderProvider>
  )
}

/**
 * Screen header for the all-jobs views.
 */
export function AllJobsViewScreenHeader() {
  return (
    <>
      <ScreenHeaderPersistentTopBar fadeWhen="title-exit">
        <ScreenHeaderPersistentTopBarLayout>
          <ScreenHeaderPersistentTopBarTitle visibility="content-exit">
            Your jobs
          </ScreenHeaderPersistentTopBarTitle>
        </ScreenHeaderPersistentTopBarLayout>
      </ScreenHeaderPersistentTopBar>
      <ScreenHeaderMain>
        <ScreenHeaderContent>
          <header className={styles.header}>
            <div className={styles.headerInner}>
              <ScreenHeaderTitle>
                <h1 className={styles.pageTitle}>Your jobs</h1>
              </ScreenHeaderTitle>
              <div className={styles.pageDescription}>
                Browse all of your current and historical jobs here.
              </div>
            </div>
          </header>
        </ScreenHeaderContent>
        <ScreenHeaderBorder visibility="always" />
      </ScreenHeaderMain>
    </>
  )
}

interface AllJobsViewContentProps {
  children?: ReactNode
}

/**
 * Page content wrapper for the all-jobs view.
 */
export function AllJobsViewContent({ children }: AllJobsViewContentProps) {
  return (
    <div className={styles.content}>
      <main className={styles.contentInner}>{children}</main>
    </div>
  )
}

interface AllJobsViewEmptyStateProps {
  startNewLabel?: string
  isStartNewAllowed?: boolean
}

/**
 * An empty state for when there are no jobs at all.
 */
export function AllJobsViewEmptyState({
  startNewLabel = 'Start new',
  isStartNewAllowed,
}: AllJobsViewEmptyStateProps) {
  return (
    <div className="pt-4xl max-w-primary-column-lg mx-auto">
      <Card>
        <CardContent>
          <CardContentDecoration spacing="2xl">
            <Icon name="nav-jobs" variant="surface-background" />
          </CardContentDecoration>
          <CardTitle>You don't have any jobs yet</CardTitle>
          <CardSubtitle>
            Whether it’s taxes, bookkeeping, or advisory services, get a Pro on
            it!
          </CardSubtitle>
          {isStartNewAllowed && (
            <CardContentFooterActions>
              <Button
                iconLeft="add"
                label={startNewLabel}
                to={links.startNewService()}
              />
            </CardContentFooterActions>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// Context for providing an `id` attribute to section headlines.
const AllJobsViewSectionHeadlineIdContext = createContext<string | undefined>(
  undefined,
)

interface AllJobsViewSectionProps {
  children?: ReactNode
}

/**
 * A section for the page content, housing a job list.
 */
export function AllJobsViewSection({ children }: AllJobsViewSectionProps) {
  const titleId = useId()
  return (
    <section className={styles.section}>
      <AllJobsViewSectionHeadlineIdContext value={titleId}>
        {children}
      </AllJobsViewSectionHeadlineIdContext>
    </section>
  )
}

interface AllJobsViewSectionHeadlineProps {
  children?: ReactNode
  count?: number
  action?: ReactNode
  isSkeleton?: boolean
}

/**
 * Headline for {@link AllJobsViewSection}s.
 */
export function AllJobsViewSectionHeadline({
  children,
  count,
  action,
  isSkeleton,
}: AllJobsViewSectionHeadlineProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const titleId = useContext(AllJobsViewSectionHeadlineIdContext)
  return (
    <SkeletonColorContext value="secondary">
      <IsSkeletonContext value={isSkeleton}>
        <SectionHeadline
          action={action}
          id={titleId}
          isSkeleton={isSkeleton}
          skeletonColor="secondary"
        >
          {children}
          {!!count && (
            <>
              {' '}
              <span className={styles.sectionHeadlineCount}>({count})</span>
            </>
          )}
        </SectionHeadline>
      </IsSkeletonContext>
    </SkeletonColorContext>
  )
}

interface AllJobsViewJobListProps {
  children?: ReactNode
}

/**
 * List of jobs.
 */
export function AllJobsViewJobList({ children }: AllJobsViewJobListProps) {
  const titleId = useContext(AllJobsViewSectionHeadlineIdContext)
  return <CardList aria-labelledby={titleId}>{children}</CardList>
}

interface AllJobsViewJobListItemProps {
  ref?: Ref<HTMLDivElement>
  title?: string
  subtitle?: string
  decoration?: ReactNode
  actions?: ReactNode
  labelChip?: ReactNode
  to?: string
  isSkeleton?: boolean
}

/**
 * All Jobs View Job List Item
 */
export function AllJobsViewJobListItem({
  title,
  subtitle,
  actions,
  decoration,
  labelChip,
  to,
  isSkeleton,
  ref,
}: AllJobsViewJobListItemProps) {
  const isSpacious = useIsSpacious()
  isSkeleton = useIsSkeleton(isSkeleton)

  const { vary, index } = useSkeletonListContext()

  return (
    <CardListItem
      ref={ref}
      to={to}
      isSkeleton={isSkeleton}
      textValue={title}
      skeletonColor="secondary"
    >
      <CardItemContent
        isInteractive={!actions}
        isSkeleton={isSkeleton}
        align="center"
        decoration={decoration}
        layout={isSpacious ? 'default' : 'stacked'}
        extras={
          Boolean(labelChip || actions) && (
            <div className="flex items-center gap-base">
              {labelChip}
              {actions}
            </div>
          )
        }
      >
        <CardTitle>
          <AutoSkeleton characters={vary(40)} isSkeleton={isSkeleton}>
            {title}
          </AutoSkeleton>
        </CardTitle>
        <CardSubtitle variant="sm">
          <AutoSkeleton
            characters={vary(index % 3 === 1 ? 20 : 60)}
            isSkeleton={isSkeleton}
          >
            {subtitle}
          </AutoSkeleton>
        </CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface AllJobsViewJobListItemAvatarDecorationProps {
  avatarUrl?: string | null
  displayName?: string
  presence?: AvatarProps['presence']
  badgeCount?: number
  isCompleted?: boolean
  isSkeleton?: boolean
}

/**
 * Avatar decoration for job list items.
 */
export function AllJobsViewJobListItemAvatarDecoration({
  avatarUrl,
  displayName,
  badgeCount,
  presence,
  isCompleted,
  isSkeleton,
}: AllJobsViewJobListItemAvatarDecorationProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <CardItemContentDecoration spacingX="base" align="center" justify="center">
      <div className={styles.itemAvatarDecoration}>
        {!isSkeleton && <FloatingBadgeCount count={badgeCount} />}

        <Avatar
          size={40}
          subject={{ avatarUrl, displayName }}
          presence={presence}
          isSkeleton={isSkeleton}
        />
        {isCompleted && !isSkeleton && <FloatingCompletedIndicator />}
      </div>
    </CardItemContentDecoration>
  )
}

interface AllJobsViewJobListItemDefaultDecorationProps {
  badgeCount?: number
  isCompleted?: boolean
  isSkeleton?: boolean
}

/**
 * Default decoration for job list items.
 */
export function AllJobsViewJobListItemDefaultDecoration({
  isCompleted,
  badgeCount,
  isSkeleton,
}: AllJobsViewJobListItemDefaultDecorationProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <CardItemContentDecoration spacingX="base" align="center" justify="center">
      <div className={styles.itemDefaultDecoration}>
        {isSkeleton ? (
          <Skeleton variant="circle" width={40} height={40} />
        ) : (
          <>
            <FloatingBadgeCount count={badgeCount} />
            <Icon skeleton={isSkeleton} sm name="nav-account" />
            {isCompleted && <FloatingCompletedIndicator />}
          </>
        )}
      </div>
    </CardItemContentDecoration>
  )
}

interface AllJobsViewJobListItemDraftActionsMenuProps {
  children?: ReactNode
  to?: string
  onDeletePress?: () => unknown
  isSkeleton?: boolean
  isPending?: boolean
}

/**
 * Actions menu for draft jobs.
 */
export function AllJobsViewJobListItemDraftActionsMenu({
  isSkeleton,
  to,
  onDeletePress,
  isPending,
}: AllJobsViewJobListItemDraftActionsMenuProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <Dropdown
      title="Actions"
      trigger={
        <IconButton
          isDisabled={isPending}
          isSkeleton={isSkeleton}
          name="more"
          label="Actions"
        />
      }
    >
      <DropdownItem icon="view" href={to}>
        <DropdownItemTitle>Open</DropdownItemTitle>
      </DropdownItem>
      <DropdownItem
        icon="delete"
        onAction={onDeletePress}
        isDisabled={isPending}
      >
        <DropdownItemTitle>Delete</DropdownItemTitle>
      </DropdownItem>
    </Dropdown>
  )
}

interface AllJobsViewJobListItemLabelChipProps {
  text: string
  variant?: 'default' | 'neutral'
  isSkeleton?: boolean
}

/**
 * Label chip for job list items.
 */
export function AllJobsViewJobListItemLabelChip({
  text,
  isSkeleton,
  variant = 'default',
}: AllJobsViewJobListItemLabelChipProps) {
  return <LabelChip isSkeleton={isSkeleton} text={text} variant={variant} />
}

/**
 * All Jobs View Content Skeleton
 */
export function AllJobsViewContentSkeleton() {
  return (
    <AllJobsViewContent>
      <AllJobsViewSection>
        <AllJobsViewSectionHeadline
          isSkeleton
          action={<Button isSkeleton variant="text" label="Placeholder" />}
        >
          Current
        </AllJobsViewSectionHeadline>
        <AllJobsViewJobList>
          <SkeletonList count={2}>
            <AllJobsViewJobListItemSkeleton withChip />
          </SkeletonList>
        </AllJobsViewJobList>
      </AllJobsViewSection>

      <AllJobsViewSection>
        <AllJobsViewSectionHeadline isSkeleton>Past</AllJobsViewSectionHeadline>
        <AllJobsViewJobList>
          <SkeletonList count={4}>
            <AllJobsViewJobListItemSkeleton />
          </SkeletonList>
        </AllJobsViewJobList>
      </AllJobsViewSection>
    </AllJobsViewContent>
  )
}

interface AllJobsViewJobListItemSkeletonProps {
  withChip?: boolean
}

/**
 * All Jobs View Job List Item Skeleton
 */
export function AllJobsViewJobListItemSkeleton({
  withChip,
}: AllJobsViewJobListItemSkeletonProps) {
  const { ref } = useSkeletonListContext()
  return (
    <AllJobsViewJobListItem
      ref={ref as Ref<HTMLDivElement>}
      title="Placeholder"
      subtitle="Placeholder"
      decoration={
        <AllJobsViewJobListItemDefaultDecoration isSkeleton isCompleted />
      }
      labelChip={
        withChip && (
          <AllJobsViewJobListItemLabelChip
            text="Placeholder"
            variant="neutral"
            isSkeleton
          />
        )
      }
    />
  )
}

interface FloatingBadgeCountProps {
  count?: number
}

/**
 * Badge with a count for use with the item decoration.
 */
function FloatingBadgeCount({ count }: FloatingBadgeCountProps) {
  return (
    <div className={styles.floatingBadgeCount}>
      <Badge hidden={!count}>{count}</Badge>
    </div>
  )
}

function FloatingCompletedIndicator() {
  return (
    <div className={styles.floatingCompletedIndicator}>
      <Icon mini sm name="done" />
    </div>
  )
}
